import { useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AmpelBadge } from "@/components/AmpelBadge"
import { Badge, Button } from "@/components/ui"
import { PruefungSheet } from "@/pages/maschinen/PruefungenPage"
import { WartungSheet } from "@/pages/maschinen/WartungenPage"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  fetchAnlage,
  fetchErsatzteile,
  fetchKomponente,
  fetchObjekt,
  fetchPruefungen,
  fetchPruefungsarten,
  fetchTickets,
  fetchWartungen,
  fetchWartungsarten,
  fetchZeitbuchungen,
  TICKET_STATUS_LABEL,
  type Ersatzteil,
  type Pruefung,
  type Ticket,
  type Wartung,
  type Zeitbuchung,
} from "@/lib/api"

type TabKey = "pruefungen" | "wartungen" | "ersatzteile" | "tickets" | "zeiten"

/**
 * Komponenten-Detail (P1 aus MASCHINEN_STRUKTURVERGLEICH.md) — Stammdaten
 * + Beziehungs-Tabs mit Zählern. Bildet das Ninox-Formularmuster nach
 * (eingebettete, klickbare Untertabellen statt Sackgasse).
 *
 * KARTEN_FAEHIGKEIT_AUDIT.md 🟡: als breite Karte umgesetzt (Dual-Muster wie
 * Ticket/Ereignis/KVP/Ersatzteil/Objekt) — `KomponenteDetailContent` wird von
 * `KomponentenPage` per Zeilen-Klick im Sheet (`SHEET_WIDTH.xl`, resizable)
 * geöffnet; `/maschinen/komponenten/:id` bleibt als Deep-Link-Fallback über
 * diese Route-Komponente erhalten. Verweise auf Prüfung/Wartung/Ersatzteil
 * öffnen deren eigene (bereits bestehende) Sheets — ein Stapel-Level.
 */
export function KomponenteDetailContent({ komponenteId }: { komponenteId: number }) {
  const [tab, setTab] = useState<TabKey>("pruefungen")
  // Issue #5: „+ Neu" je Reiter — Prüfung/Wartung als Sheet direkt hier
  // (Speichern schließt und man steht wieder in der Komponentenansicht),
  // Ersatzteil/Ticket über deren Anlege-Wege mit Rückkehr-Parameter.
  const [neuSheet, setNeuSheet] = useState<"pruefung" | "wartung" | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: komponente, isLoading, isError } = useQuery({
    queryKey: ["komponenten", komponenteId],
    queryFn: () => fetchKomponente(komponenteId),
    enabled: Number.isFinite(komponenteId),
  })

  const { data: anlage } = useQuery({
    queryKey: ["anlagen", komponente?.anlageId],
    queryFn: () => fetchAnlage(komponente!.anlageId),
    enabled: !!komponente,
  })

  const { data: objekt } = useQuery({
    queryKey: ["objekte", komponente?.objektId],
    queryFn: () => fetchObjekt(komponente!.objektId!),
    enabled: !!komponente?.objektId,
  })

  const { data: pruefungen } = useQuery({
    queryKey: ["pruefungen", "komponente", komponenteId],
    queryFn: () => fetchPruefungen({ komponenteId }),
    enabled: Number.isFinite(komponenteId),
  })
  const { data: pruefungsarten } = useQuery({
    queryKey: ["pruefungsarten"],
    queryFn: fetchPruefungsarten,
  })

  const { data: wartungen } = useQuery({
    queryKey: ["wartungen", "komponente", komponenteId],
    queryFn: () => fetchWartungen({ komponenteId }),
    enabled: Number.isFinite(komponenteId),
  })
  const { data: wartungsarten } = useQuery({
    queryKey: ["wartungsarten"],
    queryFn: fetchWartungsarten,
  })

  const { data: ersatzteile } = useQuery({
    queryKey: ["ersatzteile", "komponente", komponenteId],
    queryFn: () => fetchErsatzteile({ komponenteId }),
    enabled: Number.isFinite(komponenteId),
  })

  const { data: tickets } = useQuery({
    queryKey: ["tickets", "komponente", komponenteId],
    queryFn: () => fetchTickets({ komponenteId }),
    enabled: Number.isFinite(komponenteId),
  })

  const { data: zeitbuchungen } = useQuery({
    queryKey: ["zeitbuchungen", "komponente", komponenteId],
    queryFn: () => fetchZeitbuchungen({ komponenteId }),
    enabled: Number.isFinite(komponenteId),
  })

  const pruefungsartName = (id: number) =>
    pruefungsarten?.find((a) => a.id === id)?.pruefungsart ?? `Art #${id}`
  const wartungsartName = (id: number) =>
    wartungsarten?.find((a) => a.id === id)?.titel ?? `Art #${id}`

  // Vorbelegung für die Anlege-Sheets (Issue #5): Kette Anlage→Objekt→Komponente
  const vorbelegung = useMemo(
    () => ({
      komponenteId,
      anlageId: komponente?.anlageId ?? null,
      objektId: komponente?.objektId ?? null,
    }),
    [komponenteId, komponente?.anlageId, komponente?.objektId]
  )
  const zurueckPfad = `/maschinen/komponenten/${komponenteId}`

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const pruefungSpalten = useMemo<ColumnDef<Pruefung>[]>(
    () => [
      {
        id: "pruefungsart",
        header: "Prüfungsart",
        accessorFn: (p) => pruefungsartName(p.pruefungsartId),
        cell: ({ row }) => (
          <Link
            to={`/maschinen/pruefungen/${row.original.id}`}
            className="block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {pruefungsartName(row.original.pruefungsartId)}
          </Link>
        ),
      },
      {
        id: "pruefungsdatum",
        header: "Prüfungsdatum",
        accessorFn: (p) => p.pruefungsdatum ?? "",
        cell: ({ row }) => row.original.pruefungsdatum ?? "—",
      },
      {
        id: "ablaufdatum",
        header: "Ablaufdatum",
        accessorFn: (p) => p.ablaufdatum ?? "",
        cell: ({ row }) => row.original.ablaufdatum ?? "—",
      },
      {
        id: "ampel",
        header: "Ampel",
        accessorFn: (p) => p.ampel,
        cell: ({ row }) => <AmpelBadge ampel={row.original.ampel} />,
      },
    ],
    // pruefungsartName hängt an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pruefungsarten],
  )

  const wartungSpalten = useMemo<ColumnDef<Wartung>[]>(
    () => [
      {
        id: "wartungsart",
        header: "Wartungs-/Inspektionsart",
        accessorFn: (w) => wartungsartName(w.wartungsartId),
        cell: ({ row }) => (
          <Link
            to={`/maschinen/wartungen/${row.original.id}`}
            className="block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {wartungsartName(row.original.wartungsartId)}
          </Link>
        ),
      },
      {
        id: "wartungsdatum",
        header: "Wartungsdatum",
        accessorFn: (w) => w.wartungsdatum ?? "",
        cell: ({ row }) => row.original.wartungsdatum ?? "—",
      },
      {
        id: "ablaufdatum",
        header: "Ablaufdatum",
        accessorFn: (w) => w.ablaufdatum ?? "",
        cell: ({ row }) => row.original.ablaufdatum ?? "—",
      },
      {
        id: "ampel",
        header: "Ampel",
        accessorFn: (w) => w.ampel,
        cell: ({ row }) => <AmpelBadge ampel={row.original.ampel} />,
      },
    ],
    // wartungsartName hängt an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wartungsarten],
  )

  const ersatzteilSpalten = useMemo<ColumnDef<Ersatzteil>[]>(
    () => [
      {
        id: "bezeichnung",
        header: "Bezeichnung",
        accessorFn: (e) => e.bezeichnung,
        cell: ({ row }) => (
          <Link
            to={`/maschinen/ersatzteile/${row.original.id}`}
            className="block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {row.original.bezeichnung}
          </Link>
        ),
      },
      {
        id: "spsZuordnung",
        header: "SPS-Zuordnung",
        accessorFn: (e) => e.spsZuordnung ?? "",
        cell: ({ row }) => row.original.spsZuordnung ?? "—",
      },
    ],
    [],
  )

  const ticketSpalten = useMemo<ColumnDef<Ticket>[]>(
    () => [
      {
        id: "ticket",
        header: "Ticket",
        accessorFn: (t) => `${t.ticketKey ?? `#${t.id}`} — ${t.kurzbeschreibung}`,
        cell: ({ row }) => (
          <Link
            to={`/tickets/${row.original.id}`}
            className="block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {row.original.ticketKey ?? `#${row.original.id}`} — {row.original.kurzbeschreibung}
          </Link>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (t) => TICKET_STATUS_LABEL[t.status],
      },
      {
        id: "prioritaet",
        header: "Priorität",
        accessorFn: (t) => t.prioritaet ?? "",
        cell: ({ row }) => row.original.prioritaet ?? "—",
      },
    ],
    [],
  )

  const zeitSpalten = useMemo<ColumnDef<Zeitbuchung>[]>(
    () => [
      {
        id: "datum",
        header: "Datum",
        accessorFn: (z) => z.datum,
      },
      {
        id: "mitarbeiter",
        header: "Mitarbeiter",
        accessorFn: (z) => z.employeeId,
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.employeeId}</span>
        ),
      },
      {
        id: "stunden",
        header: "Stunden",
        accessorFn: (z) => z.stunden,
      },
      {
        id: "leistung",
        header: "Leistung",
        accessorFn: (z) => z.leistungAnzeige ?? "",
        cell: ({ row }) => row.original.leistungAnzeige ?? "—",
      },
    ],
    [],
  )

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3" aria-label="Lade Komponente …">
        <div className="h-8 w-1/3 rounded-md bg-slate-100" />
        <div className="h-24 rounded-md bg-slate-100" />
        <div className="h-40 rounded-md bg-slate-100" />
      </div>
    )
  }

  if (isError || !komponente) {
    return <p className="text-sm text-red-700">Komponente konnte nicht geladen werden.</p>
  }

  const tabs: { key: TabKey; titel: string; anzahl: number }[] = [
    { key: "pruefungen", titel: "Prüfungen", anzahl: pruefungen?.length ?? 0 },
    { key: "wartungen", titel: "Wartungen", anzahl: wartungen?.length ?? 0 },
    { key: "ersatzteile", titel: "Ersatzteile", anzahl: ersatzteile?.length ?? 0 },
    { key: "tickets", titel: "Tickets", anzahl: tickets?.length ?? 0 },
    { key: "zeiten", titel: "Zeiten", anzahl: zeitbuchungen?.length ?? 0 },
  ]

  return (
    <div>
      {/* Breadcrumb Anlage › Objekt › Komponente */}
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link
          to="/maschinen"
          className="rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Anlagen
        </Link>
        <span aria-hidden="true">›</span>
        {anlage && (
          <Link
            to={`/maschinen/anlagen/${anlage.id}`}
            className="rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {anlage.bezeichnung}
          </Link>
        )}
        {objekt && (
          <>
            <span aria-hidden="true">›</span>
            <Link
              to={`/maschinen/objekte/${objekt.id}`}
              className="rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              {objekt.bezeichnung}
            </Link>
          </>
        )}
        <span aria-hidden="true">›</span>
        <span className="text-slate-700">{komponente.beschreibung}</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">{komponente.beschreibung}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {komponente.tagNummer ?? "keine TAG-Nummer"}
          {komponente.navisionCode ? ` · Navision ${komponente.navisionCode}` : ""}
          {komponente.gesperrt ? " · gesperrt" : ""}
        </p>
      </header>

      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Stammdaten</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-slate-500">TAG-Nummer</dt>
            <dd className="font-mono text-slate-900">{komponente.tagNummer ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Navision-Code</dt>
            <dd className="font-mono text-slate-900">{komponente.navisionCode ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Identifikation</dt>
            <dd className="text-slate-900">{komponente.identifikation ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Erschwerniszulage</dt>
            <dd className="text-slate-900">{komponente.erschwerniszulage ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-slate-500">what3words</dt>
            <dd className="text-slate-900">
              {komponente.what3words ? (
                <a
                  href={`https://what3words.com/${komponente.what3words.replace(/^\/+/, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  {komponente.what3words}
                </a>
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Status</dt>
            <dd className="space-x-2">
              <Badge tone={komponente.gesperrt ? "red" : "green"}>
                {komponente.gesperrt ? "gesperrt" : "frei"}
              </Badge>
              <Badge tone="slate">
                {komponente.inVerwendung ? "in Verwendung" : "nicht in Verwendung"}
              </Badge>
            </dd>
          </div>
        </dl>
      </section>

      {/* Beziehungs-Tabs */}
      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-nowrap gap-1 overflow-x-auto border-b border-slate-200 px-4 pt-3">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`shrink-0 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                tab === t.key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.titel} ({t.anzahl})
            </button>
          ))}
        </div>

        <div className="p-4">
          {tab === "pruefungen" && (
            <div>
              <div className="mb-3 flex justify-end">
                <Button type="button" onClick={() => setNeuSheet("pruefung")}>+ Neu</Button>
              </div>
              <DataGrid
                columns={pruefungSpalten}
                data={pruefungen ?? []}
                storageKey="komponente-detail-pruefungen"
                emptyMessage="Keine Prüfungen für diese Komponente erfasst."
              />
            </div>
          )}

          {tab === "wartungen" && (
            <div>
              <div className="mb-3 flex justify-end">
                <Button type="button" onClick={() => setNeuSheet("wartung")}>+ Neu</Button>
              </div>
              <DataGrid
                columns={wartungSpalten}
                data={wartungen ?? []}
                storageKey="komponente-detail-wartungen"
                emptyMessage="Keine Wartungen für diese Komponente erfasst."
              />
            </div>
          )}

          {tab === "ersatzteile" && (
            <div>
              <div className="mb-3 flex justify-end">
                <Button type="button" onClick={() => navigate(`/maschinen/ersatzteile/neu?komponente=${komponenteId}&zurueck=${encodeURIComponent(zurueckPfad)}`)}>+ Neu</Button>
              </div>
              <DataGrid
                columns={ersatzteilSpalten}
                data={ersatzteile ?? []}
                storageKey="komponente-detail-ersatzteile"
                emptyMessage="Keine Ersatzteile für diese Komponente verbaut."
              />
            </div>
          )}

          {tab === "tickets" && (
            <div>
              <div className="mb-3 flex justify-end">
                <Button type="button" onClick={() => navigate(`/tickets?quelle=komponente&quelleId=${komponenteId}&zurueck=${encodeURIComponent(zurueckPfad)}`)}>+ Neu</Button>
              </div>
              <DataGrid
                columns={ticketSpalten}
                data={tickets ?? []}
                storageKey="komponente-detail-tickets"
                emptyMessage="Keine Tickets für diese Komponente."
              />
            </div>
          )}

          {tab === "zeiten" && (
            <div>
              <div className="mb-3 flex justify-end">
                <Button type="button" onClick={() => navigate(`/timetab?komponente=${komponenteId}`)}>+ Zeit erfassen</Button>
              </div>
              <DataGrid
                columns={zeitSpalten}
                data={zeitbuchungen ?? []}
                storageKey="komponente-detail-zeiten"
                emptyMessage="Keine Zeitbuchungen für diese Komponente."
              />
            </div>
          )}
        </div>
      </section>

      {/* Anlege-Sheets (Issue #5): öffnen direkt hier — Speichern schließt
          das Sheet und man steht wieder in der Komponentenansicht */}
      <PruefungSheet
        pruefungId={neuSheet === "pruefung" ? "neu" : null}
        onOpenChange={(open) => !open && setNeuSheet(null)}
        onGespeichert={() =>
          void queryClient.invalidateQueries({
            queryKey: ["pruefungen", "komponente", komponenteId],
          })
        }
        vorbelegung={vorbelegung}
      />
      <WartungSheet
        wartungId={neuSheet === "wartung" ? "neu" : null}
        onOpenChange={(open) => !open && setNeuSheet(null)}
        onGespeichert={() =>
          void queryClient.invalidateQueries({
            queryKey: ["wartungen", "komponente", komponenteId],
          })
        }
        vorbelegung={vorbelegung}
      />
    </div>
  )
}

/** Route-Fallback (`/maschinen/komponenten/:id`) — Deep-Link-Ziel, siehe
 * Dual-Muster in KARTEN_FAEHIGKEIT_AUDIT.md. Primärer Einstieg ist das Sheet
 * aus KomponentenPage (Zeilen-Klick). */
export function KomponenteDetailPage() {
  const params = useParams<{ id: string }>()
  const komponenteId = Number(params.id)
  return <KomponenteDetailContent komponenteId={komponenteId} />
}
